import workspace from "../../workspace";
import {findLowestRightmostPoint} from "../../../../../helpers/coordinatesHelper";
import {canShapeBeAutoShifted, getLineDirection} from "../../../../../helpers/shapeHelper";
import sqftShapeManager from "../../../store/sqftShapeManager";
import {getPointDistance} from "../../../../../helpers/sqftHelper";
import {isHorizontalDirection, isVerticalDirection} from "../../../helpers/lineHelper";
import workspaceControllers from "../../../store/workspaceControllers";
import sqftShapeIntersectionsManager from "../../../store/sqftShapeIntersectionsManager";

export default class ShapeAutoShuffle {
  __shape;

  constructor(shape) {
    this.__shape = shape;
  }

  handlePlaceShapeOnTheLine = () => {
    //Auto Shuffle
    const hoveredLines = workspace.getHoveredLines([this.__shape.id]);

    if (hoveredLines.length > 0) {
      // Get relevant shapes and lines
      const hoveredLine = this.getHoveredLine(hoveredLines);
      const hoveredShape = hoveredLine.getParent();

      if (canShapeBeAutoShifted(this.__shape, hoveredShape)) {
        const newPosition = this.calculateNewPosition(hoveredLine, hoveredShape);

        if (newPosition) {
          // Move shape to new positions
          this.moveShapeToPositionAndRotate(newPosition, hoveredLine);

          //set same snapped shape group before get shapes to shift
          workspaceControllers.snappedShapeGroups.setSameSnappedShapesGroup(this.__shape, hoveredShape);

          //Get shapes to shift
          const shapesToShift = this.getShapesToShift(hoveredShape, newPosition);
          //console.log("shapesToShift", shapesToShift);

          // Calculate new positions
          const [xDiff, yDiff] = this.calculatePositionDiffForOtherShapes(hoveredLine);
          this.moveShapesToPositions(shapesToShift, xDiff, yDiff);

          this.__shape.resetState();
          sqftShapeIntersectionsManager.updateShapesIntersections();
          workspaceControllers.snappedShapeGroups.updateShapesSnappedGroups(true);
          workspace.getHoveredLines(); // update hovered lines
          return true
        }
      }
    }
  }

  /**
   * Will return line for the lowest and rightmost shape
   * @param hoveredLines
   */
  getHoveredLine = (hoveredLines) => {
    const hoveredLinesShapes = hoveredLines.map(line => line.getParent());
    const lowestRightmostPoint = findLowestRightmostPoint(hoveredLinesShapes.map(shape => shape.getPosition()));
    const shapeToShift = hoveredLinesShapes.find(shape => {
      return shape.getPosition() === lowestRightmostPoint
        && shape.id !== this.__shape.id // skip current moving shape
    });

    return hoveredLines.find(line => line.getParent().id === shapeToShift.id);
  }

  /**
   * Will return all shapes that are lower or on the same line then point
   * @param hoveredShape
   * @param point
   */
  getShapesToShift = (hoveredShape, point) => {
    const snappedShapeGroup = hoveredShape.shapeAbsolute.getSnappedShapeGroup();
    if (snappedShapeGroup !== null && snappedShapeGroup !== undefined) {
      const snappedShapes = workspaceControllers.snappedShapeGroups.getSnappedShapes(snappedShapeGroup);
      if (snappedShapes) {
        const snappedShapesWithoutMovingShape = snappedShapes.filter(shape =>
          shape.id !== this.__shape.id
          && (hoveredShape.partId === shape.partId
            || hoveredShape.partId === shape.parentPartId
            || (hoveredShape.parentPartId && hoveredShape.parentPartId === shape.partId) //add spacers
          )
        )
        return this.findShapesRightAndBelow(snappedShapesWithoutMovingShape, point);
      } else {
        return this.findShapesRightAndBelow(hoveredShape, point);
      }
    }
    return []
  }

  /**
   * TODO diagonal
   */
  calculateNewPosition(hoveredLine, shapeToShift) {
    const shapeToShiftMeasurementPoints = sqftShapeManager.getMinMaxShapesPointsOnWorkspace([shapeToShift]);
    //console.log("shapeToShiftMeasurementPoints", shapeToShiftMeasurementPoints);
    //console.log("shapeToShift", shapeToShift);

    const hoveredLineDirection = getLineDirection(hoveredLine.from, hoveredLine.to);

    let newShapePosition = null;
    const hoveredLinePointFromPosOnWorkspace = hoveredLine.from.getPointPosOnWorkspace();
    if (isVerticalDirection(hoveredLineDirection)) {
      // Check whether it is shape start or end
      if (shapeToShiftMeasurementPoints[1].x === hoveredLinePointFromPosOnWorkspace.x) {
        newShapePosition = shapeToShiftMeasurementPoints[1];
        // Shift shape only if it is first line
      } else if (shapeToShiftMeasurementPoints[0].x === hoveredLinePointFromPosOnWorkspace.x) {
        newShapePosition = shapeToShiftMeasurementPoints[0];
      }
    } else if (isHorizontalDirection(hoveredLineDirection)) {
      // Check whether it is shape start or end
      if (shapeToShiftMeasurementPoints[0].y === hoveredLinePointFromPosOnWorkspace.y) {
        newShapePosition = shapeToShiftMeasurementPoints[0];
        // Shift shape only if it is first line
      } else if (shapeToShiftMeasurementPoints[3].y === hoveredLinePointFromPosOnWorkspace.y) {
        newShapePosition = shapeToShiftMeasurementPoints[3];
      }
    }

    return newShapePosition
  }

  /**
   *
   */
  calculatePositionDiffForOtherShapes(hoveredLine) {
    let xDiff = 0;
    let yDiff = 0;
    const thisShapeMeasurementPoints = sqftShapeManager.getMinMaxShapesPointsOnWorkspace([this.__shape]);
    const hoveredLineDirection = getLineDirection(hoveredLine.from, hoveredLine.to);
    if (isVerticalDirection(hoveredLineDirection)) {
      xDiff = getPointDistance(thisShapeMeasurementPoints[0], thisShapeMeasurementPoints[1]);
    } else if (isHorizontalDirection(hoveredLineDirection)) {
      yDiff = getPointDistance(thisShapeMeasurementPoints[3], thisShapeMeasurementPoints[0]);
    }

    return [xDiff, yDiff]
  }

  /**
   *
   */
  moveShapesToPositions(shapesToShift, xDiff, yDiff) {
    shapesToShift.forEach(shape => {
      const shapeCurrentPos = shape.getPosition();
      const shapeNewPos = {x: shapeCurrentPos.x + xDiff, y: shapeCurrentPos.y + yDiff};
      shape.setPosition(shapeNewPos);
      shape.shapeAbsolute.setAbsolutePos(shapeNewPos);
    })
  }

  /**
   * Moves and rotates the shape based on the line's orientation and the current rotation angle.
   */
  moveShapeToPositionAndRotate(newPosition, hoveredLine) {
    // Set the new position
    this.__shape.setPosition(newPosition);
    this.__shape.shapeAbsolute.setAbsolutePos(newPosition);

    // Determine the required rotation based on the line direction
    const hoveredLineDirection = getLineDirection(hoveredLine.from, hoveredLine.to);

    const isHorizontal = isHorizontalDirection(hoveredLineDirection);
    const isVertical = isVerticalDirection(hoveredLineDirection);
    const hoveredShape = hoveredLine.getParent();

    // Rotate by -90 degrees (right) if the current angle is 90 and the line is vertical
    if (hoveredShape.rotatedDegrees === 270 && isHorizontal) {
      let desiredAngle = 270;
      let normalizedCurrentAngle = ((this.__shape.rotatedDegrees % 360) + 360) % 360;
      let angleDifference = desiredAngle - normalizedCurrentAngle;
      this.__shape.rotate(angleDifference, this.__shape.Direction.LEFT, false);
      //todo
      //console.log("shape.getPosition()", this.__shape.getPosition());
      //this.moveShapesToPositions([this.__shape], -this.__shape.getWidth(), -this.__shape.getHeight());
      //console.log("shape.getPosition()", this.__shape.getPosition());
      //debugger // eslint-disable-line no-debugger
    }
    // Rotate by 90 degrees if the current angle is 0 and the line is horizontal
    else if (this.__shape.rotatedDegrees === 0 && isHorizontal) {
      this.__shape.rotate(90, this.__shape.Direction.LEFT, false);
    }
    // Rotate by -90 degrees (right) if the current angle is 90 and the line is vertical
    else if (this.__shape.rotatedDegrees === 90 && isVertical) {
      this.__shape.rotate(-90, this.__shape.Direction.LEFT, false);
    }

    // Calculate the new coordinates for the upper left corner after potential rotation
    if (isHorizontalDirection(hoveredLineDirection)) {
      // Calculate shift is not needed if the center remains in place
      const cos = Math.cos(this.__shape.rotatedDegrees * Math.PI / 180);
      const sin = Math.sin(this.__shape.rotatedDegrees * Math.PI / 180);
      const originalWidth = this.__shape.getWidth();
      const originalHeight = this.__shape.getHeight();

      // Calculate the center coordinates
      const centerX = newPosition.x + originalWidth / 2;
      const centerY = newPosition.y + originalHeight / 2;

      // Coordinates of the upper left corner after rotation
      const upperLeftX = centerX - (originalWidth / 2 * cos + originalHeight / 2 * sin);
      const upperLeftY = centerY - (originalWidth / 2 * sin - originalHeight / 2 * cos);

      // Set the new coordinates for the upper left corner
      this.__shape.setPosition({
        x: upperLeftX,
        y: upperLeftY
      });
      this.__shape.shapeAbsolute.setAbsolutePos({
        x: upperLeftX,
        y: upperLeftY
      });
    }
  }

  /**
   * Function for finding all shapes that are to the right and below a point
   * @param {Array} shapes
   * @param referencePosition
   * @returns {Array}
   */
  findShapesRightAndBelow(shapes, referencePosition) {
    const resultShapes = [];

    shapes.forEach(shape => {
      const shapeMeasurementPoints = sqftShapeManager.getMinMaxShapesPointsOnWorkspace([shape]);
      const shapeTopLeftPoint = shapeMeasurementPoints[0];

      if (shapeTopLeftPoint.x >= referencePosition.x && shapeTopLeftPoint.y >= referencePosition.y) {
        resultShapes.push(shape);
      }
    });

    return resultShapes;
  }
}
